import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Controller, useForm } from "react-hook-form";
import { useSelector, useDispatch } from "react-redux";
import Select from "react-select";
import toast from "react-hot-toast";
import { FaCheck, FaList, FaUndo } from "react-icons/fa";
import { useNavigate } from "react-router-dom";

import { handleDropDowns } from "../config/dropDownHelpers";
import {
  labelledStockTopFields,
  unlabelledStockFields,
} from "../config/fieldsHelper";
import { inflateManyToFormDefaults } from "../config/payloadFormats";

import { useAddUnlabelledStockMutation } from "../toolkitServices/unlabelledApi";
import { setUnlabelledList } from "../slices/unlabelledStockSlice";

import UnlabelStockItemForm from "./unlabelledStock/UnlabelStockItemForm";
import { useGetModuleFieldsQuery } from "../toolkitServices/moduleFieldsApi";
import QuickAddVendorModal from "./other/QuickAddVendorModal";
import VendorAvatarHoverCard from "./other/VendorAvatarHoverCard";

const defaultValue = {
  category: "", product: "", design: "", purity: "",
  totalWt: "", packingWt: "", grossWt: "", stoneWt: "", diamondWt: "",
  netWt: "", qty: "", finePercentage: "", wastagePercentage: "", fineWastageWt: "",
};

const pickCreated = (res) => {
  if (!res) return [];
  if (Array.isArray(res)) return res;
  if (Array.isArray(res?.data)) return res.data;
  if (Array.isArray(res?.entries)) return res.entries;
  if (res?.data && typeof res.data === "object" && !Array.isArray(res.data)) return [res.data];
  if (res?.entry && typeof res.entry === "object") return [res.entry];
  return [];
};

const UnlabelledStockForm = ({
  existingData = null,
  onSubmitOverride,
  submitLabel = "Save",
}) => {
   const { data: { data: unlblStkFields = [] } = {}, isLoading:unlabelledFieldLoading } = useGetModuleFieldsQuery("UNLABELLED_STOCK");
    const unlabelledStockFields = useMemo(
    () => unlblStkFields.filter((f) => f.visibility !== false),
    [unlblStkFields]
  );
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const firstFieldRef = useRef(null);
  const vendorSelectRef = useRef(null);
  const [quickVendorOpen, setQuickVendorOpen] = useState(false);

  const categories = useSelector((s) => s.categories?.categories);
  const products = useSelector((s) => s.products?.products);
  const vendors = useSelector((s) => s.vendors?.vendors);
  const designs = useSelector((s) => s.designs?.designs);
  const purities = useSelector((s) => s.purities?.purities);
  const branches = useSelector((s) => s.branches?.branches);
  const counters = useSelector((s) => s.counters?.counters);

  const unlabelledStocks = useSelector((s) => s?.unlabelledStocks?.unlabelledStock) || [];

  const {
    control,
    handleSubmit,
    register,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm({
    shouldUnregister: true,
    defaultValues: existingData || {
      vendor: "",
      branch: "",
      counter: "", // 🔥 NEW
      stock: defaultValue,
    },
  });

  // async existing data prefill
  useEffect(() => {
    if (existingData) {
      // ensure counter default if missing
      const patched = { counter: "", ...existingData };
      reset(patched);
      requestAnimationFrame(() =>
        setTimeout(() => firstFieldRef.current?.focus?.(), 0)
      );
    }
  }, [existingData, reset]);

  const [addUnlabelledStock, { isLoading }] = useAddUnlabelledStockMutation();

  // hotkeys
  useEffect(() => {
    const onKeys = (e) => {
      if (e.ctrlKey && e.key.toLowerCase() === "s") {
        e.preventDefault();
        handleSubmit(onSubmit)();
      }
      if (e.ctrlKey && e.key.toLowerCase() === "r") {
        e.preventDefault();
        reset({ vendor: "", branch: "", counter: "", stock: defaultValue }); // 🔥 include counter
        requestAnimationFrame(() =>
          setTimeout(() => firstFieldRef.current?.focus?.(), 0)
        );
      }
    };
    window.addEventListener("keydown", onKeys);
    return () => window.removeEventListener("keydown", onKeys);
  }, [handleSubmit, reset]);

  const onSubmit = async (form) => {
    if (typeof onSubmitOverride === "function") {
      return onSubmitOverride(form);
    }

    const { vendor, branch, counter, stock } = form || {};
    if (!vendor?.value) return toast.error("Vendor is required");
    if (!branch?.value) return toast.error("Branch is required");
    if (!counter?.value) return toast.error("Counter is required"); // 🔥 NEW
    if (!stock?.category?.value) return toast.error("Category is required");
    if (!stock?.product?.value) return toast.error("Product is required");
    if (!stock?.design?.value) return toast.error("Design is required");
    if (!stock?.purity?.value) return toast.error("Purity is required");
    if (!(+(stock?.totalWt)||0)) return toast.error("Total Wt is required");
    if (!(+(stock?.qty)||0)) return toast.error("Qty is required");

    try {
      // backend ab counter expect karta hai (we already added in controller)
      const res = await addUnlabelledStock(form).unwrap();
      const created = pickCreated(res);
      const failed  = res?.failedItems || res?.failed || [];

      if (created?.length) {
        toast.success(`${created.length} unlabelled item${created.length > 1 ? "s" : ""} added`);
        if (unlabelledStocks) {
          dispatch(setUnlabelledList([...created, ...unlabelledStocks]));
        }
        reset({ vendor: "", branch: "", counter: "", stock: defaultValue }); // 🔥 reset counter too
        requestAnimationFrame(() =>
          setTimeout(() => firstFieldRef.current?.focus?.(), 0)
        );
        return;
      }

      if (failed?.length) {
        toast.error("Some entries failed. Please review.");
        const inflated = inflateManyToFormDefaults(failed) || [];
        const first = inflated?.[0];
        reset({
          vendor: failed?.[0]?.vendorId ? { value: failed[0].vendorId, label: failed[0].vendorName } : null,
          branch: failed?.[0]?.branchId ? { value: failed[0].branchId, label: failed[0].branchName } : null,
          counter: failed?.[0]?.counterId ? { value: failed[0].counterId, label: failed[0].counterName } : null, // 🔥
          stock: first || defaultValue,
        });
        requestAnimationFrame(() =>
          setTimeout(() => firstFieldRef.current?.focus?.(), 0)
        );
        return;
      }

      toast.success("Saved.");
    } catch (err) {
      const msg =
        err?.data?.message || err?.data?.msg || err?.error || "Error adding unlabelled stock";
      const failed = err?.data?.failedItems || err?.data?.failed || [];
      if (failed?.length) {
        toast.error("Some entries failed. Please review.");
        const inflated = inflateManyToFormDefaults(failed) || [];
        const first = inflated?.[0];
        reset({
          vendor: failed?.[0]?.vendorId ? { value: failed[0].vendorId, label: failed[0].vendorName } : null,
          branch: failed?.[0]?.branchId ? { value: failed[0].branchId, label: failed[0].branchName } : null,
          counter: failed?.[0]?.counterId ? { value: failed[0].counterId, label: failed[0].counterName } : null, // 🔥
          stock: first || defaultValue,
        });
        requestAnimationFrame(() =>
          setTimeout(() => firstFieldRef.current?.focus?.(), 0)
        );
      } else {
        toast.error(msg);
      }
    }
  };

  // selected branch -> filter counters
  const selectedVendor = watch("vendor");
  const selectedBranch = watch("branch");
 
  return (
    <>
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
      {/* Top inputs */}
      <div className="bg-white shadow-sm rounded-xl px-3 py-2 border">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3">
          {labelledStockTopFields?.map((item, idx) => (
            <div
              key={idx}
              className={`min-w-0 ${item.name === "vendor" ? "sm:col-span-2" : ""}`}
            >
              <label className="block text-xs font-semibold text-slate-600 mb-1">
                {item?.label} {item?.mandatory && <span className="text-red-500">*</span>}
              </label>

              {item?.type === "text" && (
                <>
                  <input
                    {...register(item.name, { required: item?.mandatory ? `${item.label} is required` : false })}
                    placeholder={`Enter ${item.label}`}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                    ref={idx === 0 ? firstFieldRef : null}
                  />
                  {errors[item.name] && (
                    <p className="text-red-500 text-[11px] mt-1">{errors[item.name].message}</p>
                  )}
                </>
              )}

              {item?.type === "number" && (
                <>
                  <input
                    type="number"
                    {...register(item.name, { required: item?.mandatory ? `${item.label} is required` : false })}
                    placeholder={`Enter ${item.label}`}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  {errors[item.name] && (
                    <p className="text-red-500 text-[11px] mt-1">{errors[item.name].message}</p>
                  )}
                </>
              )}

              {item?.type === "date" && (
                <>
                  <input
                    type="date"
                    {...register(item.name, { required: item?.mandatory ? `${item.label} is required` : false })}
                    className="w-full px-3 py-2 border rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-violet-400"
                  />
                  {errors[item.name] && (
                    <p className="text-red-500 text-[11px] mt-1">{errors[item.name].message}</p>
                  )}
                </>
              )}

              {item?.type === "dropdown" && (
                <>
                  <Controller
                    name={item.name}
                    control={control}
                    rules={{ required: item?.mandatory ? `${item.label} is required` : false }}
                    render={({ field }) => (
                      <div className={item.name === "vendor" ? "flex items-center gap-2" : ""}>
                        <div className="min-w-0 flex-1">
                          <Select
                            {...field}
                            ref={item.name === "vendor" ? vendorSelectRef : null}
                            options={handleDropDowns({
                              key: item["name"],
                              vendors,
                              categories,
                              products,
                              designs,
                              purities,
                              category: "",
                              product: "",
                              branches,
                              counters,
                              branch: selectedBranch,
                            })}
                            classNamePrefix="react-select"
                            placeholder={`Select...`}
                            isClearable
                            menuPlacement="auto"
                          />
                        </div>
                        {item.name === "vendor" && !existingData && (
                          <button
                            type="button"
                            onClick={() => setQuickVendorOpen(true)}
                            className="shrink-0 rounded-md border px-4 py-1.5 text-xs text-[#6b59c6] hover:bg-violet-50 focus:outline-none focus:ring-2 focus:ring-[#9481ce]"
                            title="Quick add vendor"
                            aria-label="Quick add vendor"
                          >
                            +
                          </button>
                        )}
                        {item.name === "vendor" && (
                          <VendorAvatarHoverCard vendor={selectedVendor} />
                        )}
                      </div>
                    )}
                  />
                  {errors[item.name] && (
                    <p className="text-red-500 text-[11px] mt-1">{errors[item.name].message}</p>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Single Item Card */}
      <div className="bg-white p-0">
        <UnlabelStockItemForm
          basePath="stock"
          control={control}
          watch={watch}
          fields={unlabelledStockFields}
          register={register}
          errors={errors}
          setValue={setValue}
          firstFieldRef={firstFieldRef}
        />

        {/* Actions */}
       <div className="p-2 flex flex-wrap gap-2 justify-end items-center">
  {/* Save */}
  <motion.button
    whileHover={{ scale: isLoading ? 1 : 1.02, y: isLoading ? 0 : -1 }}
    whileTap={{ scale: isLoading ? 1 : 0.98, y: 0 }}
    type="submit"
    disabled={isLoading}
    className="group inline-flex items-center gap-2 rounded-full bg-emerald-500 px-4 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-emerald-600 hover:shadow-md transition-all disabled:opacity-70 disabled:cursor-not-allowed"
  >
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
      <FaCheck className="text-[10px]" />
    </span>
    <span className="tracking-wide">
      {isLoading ? "Please wait..." : submitLabel}
    </span>

    <span className="ml-1 h-4 w-px bg-emerald-300/80 group-hover:bg-white/60" />

    <span className="inline-flex items-center rounded-full bg-emerald-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
      Ctrl + S
    </span>
  </motion.button>

  {/* Reset */}
  <motion.button
    whileHover={{ scale: 1.02, y: -1 }}
    whileTap={{ scale: 0.98, y: 0 }}
    type="button"
    onClick={() => {
      reset(
        existingData || {
          vendor: "",
          branch: "",
          counter: "",
          stock: defaultValue,
        }
      );
      requestAnimationFrame(() =>
        setTimeout(() => firstFieldRef.current?.focus?.(), 0)
      );
    }}
    className="group inline-flex items-center gap-2 rounded-full border border-slate-300 bg-slate-50 px-3.5 py-1.5 text-[11px] font-semibold text-slate-700 shadow-sm hover:bg-slate-100 hover:border-slate-400 hover:shadow-md transition-all"
  >
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-slate-700 text-white">
      <FaUndo className="text-[10px]" />
    </span>
    <span className="tracking-wide">Reset</span>

    <span className="ml-1 h-4 w-px bg-slate-200 group-hover:bg-slate-300" />

    <span className="inline-flex items-center rounded-full bg-white/80 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide text-slate-700">
      Ctrl + R
    </span>
  </motion.button>

  {/* List */}
  <motion.button
    whileHover={{ scale: 1.02, y: -1 }}
    whileTap={{ scale: 0.98, y: 0 }}
    type="button"
    onClick={() => navigate("/unlabelled-stock")}
    className="group inline-flex items-center gap-2 rounded-full bg-indigo-500 px-3.5 py-1.5 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600 hover:shadow-md transition-all"
  >
    <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-white/15">
      <FaList className="text-[10px]" />
    </span>
    <span className="tracking-wide">Unlabelled Stock List</span>

    <span className="ml-1 h-4 w-px bg-indigo-300/80 group-hover:bg-white/60" />

    <span className="inline-flex items-center rounded-full bg-indigo-400/90 px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wide">
      Ctrl + ⌫
    </span>
  </motion.button>
</div>

      </div>
    </form>
    <QuickAddVendorModal
      open={quickVendorOpen}
      onClose={() => setQuickVendorOpen(false)}
      onAdded={(created) => {
        const option = (
          handleDropDowns({ key: "vendor", vendors: [created] }) || []
        )[0];
        if (option) {
          setValue("vendor", option, {
            shouldDirty: true,
            shouldValidate: true,
          });
        }
        requestAnimationFrame(() => {
          setTimeout(() => vendorSelectRef.current?.focus?.(), 80);
        });
      }}
    />
    </>
  );
};

export default UnlabelledStockForm;
